<template>
    <div class="dropdown">
        <input type="text"
               class="form-control location-control"
               :placeholder="placeholder"
               v-model="searchQuery"
               ref="input"
               @keydown.enter="enter"
               @keydown.down="down"
               @keydown.up="up"
               @focus="focus"
               @blur="blur"
        >
        <div class="dropdown-menu d-block" v-if="(groups.length > 0 && focused) || isLoading" style="min-width: 100%">
            <div class="dropdown-item-text fa-2x" v-if="isLoading">
                <i class="fas fa-circle-notch fa-spin"></i>
            </div>
            <template v-for="(group, index) in groups">
                <div class="dropdown-divider" v-if="index !== 0"></div>
                <div class="dropdown-item-text"><strong>{{ group.text }}</strong></div>
                <a class="dropdown-item"
                   v-for="item in group.items"
                   ref="results"
                   :class="{'active text-white': itemsById[item.id].index === current}"
                   @click="selectOption(itemsById[item.id].index)"
                >{{ item.name }}</a>
            </template>
        </div>
    </div>
</template>

<script lang="ts">
import {Location} from '@/model/api';
import {SearchResult, SearchResultGroup, SearchResultItem} from '@/model/api/search';
import * as Sentry from '@sentry/vue';
import {debounce} from 'lodash-es';
import {computed, defineComponent, nextTick, PropType, ref, watch} from 'vue';

interface ItemWithIndex { index: number; item: SearchResultItem; }

export default defineComponent({
    props: {
        search: {
            type: Function as PropType<(searchQuery: string) => Promise<SearchResult<Location>>>,
            required: true,
        },
        value: {
            type: Object as PropType<SearchResultItem | null>,
            default: null,
        },
        placeholder: {
            type: String,
            default: '',
        },
    },
    emits: ['input'],
    setup(props, {emit, expose}) {
        const input = ref<HTMLElement | null>(null);
        const searchQuery = ref('');
        const groups = ref<SearchResultGroup[]>([]);
        const isLoading = ref(false);
        const focused = ref(false);
        const current = ref(0);

        let preventSearch = false;

        const itemsById = computed(() => {
            const items: { [id: string]: ItemWithIndex } = {};
            let index = 0;

            groups.value.forEach((group) => {
                group.items.forEach((item) => {
                    items[item.id] = {index, item};
                    index++;
                });
            });

            return items;
        });

        function isEmpty(): boolean {
            return props.value === null;
        }

        function updateResults(): void {
            if (preventSearch) {
                preventSearch = false;
                return;
            }

            if (searchQuery.value.trim().length < 2) {
                return;
            }

            groups.value = [];
            isLoading.value = true;

            props.search(searchQuery.value)
                .then((result) => {
                    groups.value = result.groups.filter((group) => group.items.length > 0);
                    current.value = 0;
                })
                .catch((error) => {
                    Sentry.captureException(error);
                    groups.value = [];
                })
                .finally(() => {
                    nextTick(() => isLoading.value = false);
                });
        }

        const lazySearch = debounce(updateResults, 1000);

        // created logic
        if (props.value) {
            searchQuery.value = props.value.name;
        }

        watch(searchQuery, () => {
            if (searchQuery.value === '') {
                emit('input', null);
                return;
            }

            lazySearch();
        });

        function selectOption(index: number): void {
            const value = Object.values(itemsById.value).find((item) => item.index === index)!.item;
            preventSearch = true;
            searchQuery.value = value.name;
            input.value?.blur();

            emit('input', value);
        }

        function up(): void {
            current.value = current.value === 0 ? Object.keys(itemsById.value).length : current.value - 1;
        }

        function down(): void {
            current.value = current.value === Object.keys(itemsById.value).length ? 0 : current.value + 1;
        }

        function enter(): void {
            if (groups.value.length === 0) {
                return;
            }

            selectOption(current.value);
        }

        function focus(): void {
            focused.value = true;
        }

        function blur(): void {
            setTimeout(() => focused.value = false, 200);
        }

        expose({isEmpty, updateResults});

        return {
            input, searchQuery, groups, isLoading, focused, current,
            itemsById, selectOption, up, down, enter, focus, blur,
        };
    },
});
</script>
